'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTheme } from '@/context/theme-context';
import { useProductDesignOverride } from '@/context/product-design-context';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { NO_COSMIC_EFFECTS, resolveCartFlight, resolveCosmicEffects, type CosmicEffects } from '@/lib/cosmic-effects';
import type { GradientDirection } from '@/lib/background-prefs';

export type CosmicEffectsState = {
  /** The starfield canvas. Also THE flag for the seller constellations. */
  stars: boolean;
  planets: boolean;
  gradient: boolean;
  /** Light mode's dot grid (src/components/ui/dot-grid.tsx). */
  grid: boolean;
  /** The add-to-cart shooting star, which has its own gate (resolveCartFlight): it is feedback, not sky. */
  cartFlight: boolean;
  /** The effective gradient colour as '#rrggbb' (also painted as the --gradient CSS variable). */
  gradientColor: string;
  gradientDirection: GradientDirection;
  /** The theme the page is painted in, a seller's forced theme included. */
  theme: 'dark' | 'light';
  /**
   * The open page paints a seller's own background colour, so the gradient
   * rises from that colour rather than from black (ClientLayout).
   */
  customBackground: boolean;
};

/**
 * The background effects that are on for this viewer on this page. Every
 * cosmic consumer reads this rather than the raw toggles, so none of them can
 * disagree about checkout, the per-theme switches, reduced motion or a seller
 * override. `theme` tells a consumer which palette to draw in: white on the
 * dark sky, ink on the light page.
 * The rules and why they are what they are: src/lib/cosmic-effects.ts.
 *
 * Its own `mounted` flag rather than one passed down: each caller renders on
 * the server too, and every one of them must render nothing until after
 * hydration. A caller mounted later (a constellation on a page navigated to)
 * is off for exactly one frame, which is invisible.
 *
 * Its own `held` value for the same reason: what to keep showing while the
 * page's seller override is still loading is what THIS caller last showed. The
 * shell's instance outlives a navigation and so carries the previous page's
 * effects across; a constellation mounted on the new page starts from nothing,
 * so it cannot twinkle behind a seller who turned the starfield off.
 */
export function useCosmicEffects(): CosmicEffectsState {
  const {
    resolvedTheme,
    showStarfield, showGradient, showPlanets,
    showPlanetsInLight, showDotGrid,
    backgroundPrefsReady,
    gradientColor, gradientDirection,
  } = useTheme();
  const { override, overridePending } = useProductDesignOverride();
  const pathname = usePathname();
  const reducedMotion = useReducedMotion();

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Read in render, written only after a commit, so a render React throws away
  // can never become the value held.
  const heldRef = useRef<CosmicEffects>(NO_COSMIC_EFFECTS);

  const { stars, planets, gradient, grid } = resolveCosmicEffects({
    mounted,
    prefsReady: backgroundPrefsReady,
    overridePending,
    held: heldRef.current,
    pathname,
    theme: resolvedTheme,
    reducedMotion,
    override,
    viewer: { showStarfield, showGradient, showPlanets, showPlanetsInLight, showDotGrid },
  });

  // Only a settled answer is worth holding: while an override is pending the
  // held value is left alone, since the answers computed from it are the held
  // value again, trimmed by the theme and motion setting of the moment.
  useEffect(() => {
    if (!overridePending) heldRef.current = { stars, planets, gradient, grid };
  }, [overridePending, stars, planets, gradient, grid]);

  return {
    stars,
    planets,
    gradient,
    grid,
    cartFlight: resolveCartFlight({ mounted, pathname, reducedMotion }),
    gradientColor,
    gradientDirection,
    theme: resolvedTheme,
    customBackground: !!override?.backgroundColor,
  };
}
